<template>
    <div class="p-day-one__form">
        <control label="Disease or Accident" :icon-space="false">
            <select v-model="diseaseOrAccident">
                <option :value="null" hidden>Please select...</option>
                <option value="disease">Disease</option>
                <option value="accident">Accident</option>
            </select>
        </control>

        <control label="Disease Type" :icon-space="false" v-if="diseaseOrAccident === 'disease'">
            <select v-model="diseaseType">
                <option :value="null" hidden>Please select...</option>
                <option value="Asbestos Related Lung Cancer">Asbestos Related Lung Cancer</option>
                <option value="Bladder Cancer">Bladder Cancer</option>
                <option value="Kidney Cancer">Kidney Cancer</option>
                <option value="Laryngeal Cancer">Laryngeal Cancer</option>
                <option value="Leukemia">Leukemia</option>
                <option value="Liver Cancer">Liver Cancer</option>
                <option value="Mesothelioma">Mesothelioma</option>
                <option value="Nasal Cancer">Nasal Cancer</option>
                <option value="Skin Cancer">Skin Cancer</option>
                <option value="Other">Other</option>
                <option value="Other Cancer">Other Cancer</option>
            </select>
        </control>

        <control label="Accident Type" :icon-space="false" v-if="diseaseOrAccident === 'accident'">
            <select v-model="accidentType">
                <option :value="null" hidden>Please select...</option>
                <option value="Accident - EL">Accident - EL</option>
                <option value="Accident - PL">Accident - PL</option>
                <option value="Motor">Motor</option>
                <option value="Other">Other</option>
            </select>
        </control>

        <control label="Gender of Claimant" :icon-space="false">
            <select v-model="gender">
                <option :value="null" hidden>Please Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
            </select>
        </control>

        <control label="Claimant's DOB" :icon-space="false">
            <date-input v-model="dateOfBirth" v-field-control:dob="dobRules"></date-input>
            <control-error :show="$error('dob')">
                The person must be born in the past
            </control-error>
        </control>

        <control label="Onset of Symptoms" :icon-space="false">
            <date-input v-model="onsetOfSymptoms" v-field-control:osos="oSoSRules"></date-input>
            <control-error :show="$error('osos')">
                The symptoms must be before the date of death
            </control-error>
        </control>

        <control label="Date of Assessment (or assumed)" :icon-space="false">
            <date-input v-model="assumedSettlement" v-field-control:assumed="assumedRules"></date-input>
            <control-error :show="$error('assumed')">
                The assessment date must be after death.
            </control-error>
        </control>

        <control label="Claimant's Assumed Life Expectancy" :icon-space="false">
            <select name="claimants_assumed_dod_type"
                    id="claimants_assumed_dod_type"
                    @change="updateAssumedLifeExpectancy"
                    v-model="assumedLifeExpectancyMethod"
                    v-field-control
                    required>
                <option :value="null" disabled hidden>Please select...</option>
                <option value="assumed_years_and_months_left">Enter Years/Months</option>
                <option value="assumed_date">Enter Date</option>
                <option value="assumed_age_at_death">Enter Age at Death (years)</option>
                <option value="assumed_percentage_of_life">% of Normal Life Expectancy</option>
                <option value="assumed_reduction_in_life">% Reduction in Normal Life Expectancy</option>
            </select>
        </control>

        <control label="Claimant's Age at Assumed Death" :icon-space="false"
                 v-if="assumedLifeExpectancyMethod === 'assumed_age_at_death'"
                 before-after="years-old">
            <input autocomplete="off" v-field-control
                   required
                   :disabled="assumedLifeExpectancyMethod !== 'assumed_age_at_death'"
                   name="claimants_age_at_assumed_death"
                   v-model.number="assumedAgeAtDeath"
                   v-two-dp
                   @input="updateAssumedLifeExpectancy"
                   type="number">
            <control-error :show="minAgeRules">
                The claimant's age at death is required and should be at least {{ currentAge }}
            </control-error>
        </control>

        <control :icon-space="false"
                 label="Enter Years/Months From Today"
                 v-if="assumedLifeExpectancyMethod === 'assumed_years_and_months_left'">
            <div class="a-form-inputs--half">
                <div class="a-form-inputs__container">
                    <select id="years" v-model.number="yearsLeft"
                            @change="updateAssumedLifeExpectancy">
                        <option v-for="year in 76" :value="year - 1">{{ year - 1}} year(s)</option>
                    </select>
                </div>
                <div class="a-form-inputs__container">
                    <select id="months" v-model.number="monthsLeft"
                            @change="updateAssumedLifeExpectancy">
                        <option v-for="month in 12" :value="month - 1">
                            {{ month - 1 }} month(s)
                        </option>
                    </select>
                </div>
            </div>
        </control>

        <control :icon-space="false" label="Percentage of Normal Life Expectancy"
                 v-if="assumedLifeExpectancyMethod == 'assumed_percentage_of_life'"
                 before-after="percent">
            <input autocomplete="off"
                   v-model.number="innerAssumedLifeExpectancy.percentageOfNormalLifeExpectancy"
                   :disabled="assumedLifeExpectancyMethod != 'assumed_percentage_of_life'"
                   v-field-control
                   v-two-dp
                   required
                   max="100"
                   min="1"
                   @input="updateAssumedLifeExpectancy"
                   name="percentage_of_normal_life_expectancy"
                   type="number">
            <control-error :show="$error('percentage_of_normal_life_expectancy')">
                The percentage of normal life expectancy must be number between 1 and 100
            </control-error>
        </control>

        <control label="Percentage Reduction in Normal Life Expectancy"
                 :icon-space="false"
                 v-if="assumedLifeExpectancyMethod == 'assumed_reduction_in_life'"
                 before-after="percent">
            <input autocomplete="off"
                   v-model.number="innerAssumedLifeExpectancy.percentageReductionInNormalLifeExpectancy"
                   :disabled="assumedLifeExpectancyMethod != 'assumed_reduction_in_life'"
                   v-field-control
                   required
                   max="100"
                   min="1"
                   @input="updateAssumedLifeExpectancy"
                   name="percentage_reduction_in_normal_life_expectancy"
                   type="number">
            <control-error :show="$error('percentage_reduction_in_normal_life_expectancy')">
                The percentage reduction in normal life expectancy must be number between 1 and 100
            </control-error>
        </control>

        <control :icon-space="false" label="Claimant's Assumed Date of Death">
            <date-input v-model="assumedDateOfDeath"
                        v-field-control:claimants_assumed_date_of_death.required="{type:'date'}"
                        @input="updateAssumedLifeExpectancy"
                        :disabled="assumedLifeExpectancyMethod !== 'assumed_date'"
            ></date-input>
        </control>

        <!--<life-expectancy-->
                <!--label="Claimant's 'But For' Life Expectancy"-->
                <!--:person="getValue('claimant')"-->
                <!--:date="theDate"-->
                <!--:dayOne="true"-->
                <!--type="deceased"-->
                <!--@update="updateLife"-->
        <!--&gt;</life-expectancy>-->

        <control label="Employed or Retired" :icon-space="false">
            <select v-model="employmentType">
                <option :value="null" hidden>Please Select...</option>
                <option value="employed">Employed</option>
                <option value="retired">Retired</option>
            </select>
        </control>

        <div v-if="employmentType=='employed'">
            <control label="Gross Annual Salary" before-after="pound" :icon-space="false">
                <input type="text" min="0" v-model="salary">
            </control>
            <salary-finder v-if="gender" :gender="gender" @update="updateSalary"></salary-finder>
        </div>

        <control label="Procedural Status" :icon-space="false">
            <select v-model="proceduralStatus">
                <option :value="null" hidden>Please Select...</option>
                <option value="pre-lit">Pre-Litigation</option>
                <option value="litigated">Litigated</option>
            </select>
        </control>

        <div v-if="showErrorMessage">
            <span class="a-form-inputs--error">You must complete all fields.</span>
        </div>
        Calculate the results:
        <div>
            <button class="a-button u-no-margin" @click="calculate">Calculate</button>
            <button class="a-button u-no-margin" @click="reset">Reset</button>
        </div>
    </div>
</template>

<script>
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import CurrencyFormat from 'mixins/currencyFormat';
    import DateInput from 'components/form/control/DateInput.vue';
    import SalaryFinder from 'pages/reserving/components/SalaryFinder';
    import {mapActions, mapGetters} from 'vuex';
    import moment from 'moment';
    import _ from 'lodash';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import LifeExpectancy from 'components/LifeExpectancy.vue';

    export default {
        name: 'day-one-fatal',
        components: {Control, ControlError, FormValidator, DateInput, SalaryFinder, LifeExpectancy},
        mixins: [
            FormValidator,
            CurrencyFormat
        ],
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        mounted() {
            this.reset();
        },
        data() {
            return {
                theDate: moment().format('DD/MM/YYYY'),
                showErrorMessage: false
            }
        },
        computed: {
            ...mapGetters('reserves/dayOneLost', [
                'isValid'
            ]),
            ...mapGetters('windowWidth', ['theCurrentWidth']),
            innerLifeExpectancy() {
                return _.cloneDeep(this.getValue('lifeExpectancy'));
            },
            innerAssumedLifeExpectancy() {
                return _.cloneDeep(this.getValue('assumedLifeExpectancy'));
            },
            currentAge() {
                return moment().diff(moment(this.dateOfBirth, 'DD/MM/YYYY'), 'years');
            },
            minAgeRules() {
                if (!this.assumedAgeAtDeath) {
                    return false;
                }

                return (moment().diff(moment(this.dateOfBirth, 'DD/MM/YYYY'), 'years') > this.assumedAgeAtDeath);
            },
            diseaseOrAccident: {
                get() {
                    return this.getValue('diseaseOrAccident');
                },
                set(value) {
                    return this.updateValue({key: 'diseaseOrAccident', value});
                }
            },
            diseaseType: {
                get() {
                    return this.getValue('diseaseType');
                },
                set(value) {
                    this.updateValue({key: 'accidentType', value: null});
                    return this.updateValue({key: 'diseaseType', value});
                }
            },
            accidentType: {
                get() {
                    return this.getValue('accidentType');
                },
                set(value) {
                    this.updateValue({key: 'diseaseType', value: null});
                    return this.updateValue({key: 'accidentType', value});
                }
            },
            gender: {
                get() {
                    return this.getValue('gender');
                },
                set(value) {
                    return this.updateValue({key: 'gender', value});
                }
            },
            dateOfBirth: {
                get() {
                    return this.getValue('dateOfBirth');
                },
                set(value) {
                    return this.updateValue({key: 'dateOfBirth', value});
                }
            },
            dateOfDeath: {
                get() {
                    return this.getValue('dateOfDeath');
                },
                set(value) {
                    return this.updateValue({key: 'dateOfDeath', value});
                }
            },
            ageAtDeath: {
                get() {
                    return this.getValue('ageAtDeath');
                },
                set(value) {
                    return this.updateValue({key: 'ageAtDeath', value});
                }
            },
            assumedSettlement: {
                get() {
                    return this.getValue('assumedSettlement');
                },
                set(value) {
                    return this.updateValue({key: 'assumedSettlement', value});
                }
            },
            employmentType: {
                get() {
                    return this.getValue('employmentType');
                },
                set(value) {
                    return this.updateValue({key: 'employmentType', value});
                }
            },
            onsetOfSymptoms: {
                get() {
                    return this.getValue('onsetOfSymptoms');
                },
                set(value) {
                    return this.updateValue({key: 'onsetOfSymptoms', value});
                }
            },
            proceduralStatus: {
                get() {
                    return this.getValue('proceduralStatus');
                },
                set(value) {
                    return this.updateValue({key: 'proceduralStatus', value});
                }
            },
            assumedLifeExpectancyMethod: {
                get() {
                    return this.getValue('assumedLifeExpectancy').method;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.method = value;
                }
            },
            yearsLeft: {
                get() {
                    return this.getValue('assumedLifeExpectancy').yearsLeft;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.yearsLeft = value;
                }
            },
            monthsLeft: {
                get() {
                    return this.getValue('assumedLifeExpectancy').monthsLeft;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.monthsLeft = value;
                }
            },
            assumedDateOfDeath: {
                get() {
                    return this.getValue('assumedLifeExpectancy').dateOfDeath;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.dateOfDeath = value;
                }
            },
            assumedAgeAtDeath: {
                get() {
                    return this.getValue('assumedLifeExpectancy').ageAtDeath;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.ageAtDeath = value;
                }
            },
            dobRules() {
                return {
                    type: "date",
                    rules: 'max:' + moment().format('DD/MM/YYYY')
                }
            },
            dodRules() {
                return {
                    type: "date",
                    rules: 'min:' + this.dateOfBirth
                }
            },
            assumedRules() {
                return {
                    type: "date",
                    rules: 'min:' + this.dateOfDeath
                }
            },
            oSoSRules() {
                return {
                    type: "date",
                    rules: 'max:' + this.dateOfDeath
                }
            },
            occupation() {
                return this.getValue('occupation');
            },
            salary: {
                get() {
                    return this.getValue('salary');
                },
                set(value) {
                    return this.updateValue({key: 'salary', value});
                }
            }
        },
        methods: {
            ...mapActions('reserves/dayOneLost', [
                'updateValue',
                'performCalculation',
                'reset',
                'updateClaimantAssumedLifeExpectancy',
                'updateLifeExpectancy'
            ]),
            getValue(attr) {
                return this.$store.getters['reserves/dayOneLost/' + attr];
            },
            calculate() {
                this.showErrorMessage = !this.isValid || !this.$valid();
                if (!this.showErrorMessage) {
                    // assign column one to a variable
                    let assignColumnOne = document.querySelector('.c-split-screen__column-one');
                    // get the bound elements of column one so we can target it's height
                    var elementBounding = assignColumnOne.getBoundingClientRect();
                    if(this.theCurrentWidth <= 1024) {
                        // scroll to the height of column one (plus the heights for passport and the header) which puts us at the top of column two
                        window.scrollTo(0, elementBounding.height + 108);
                    } else {
                        window.scrollTo(0, 0);
                    }

                    this.performCalculation();
                }
            },
            updateSalary(value) {
                return Promise.all([
                    this.updateValue({key: 'occupation', value: value.occupation}),
                    this.updateValue({key: 'salary', value: value.salary}),
                ]);
            },
            updateAssumedLifeExpectancy() {
                let assumedLifeExpectancy = this.innerAssumedLifeExpectancy;
                this.updateClaimantAssumedLifeExpectancy({assumedLifeExpectancy});
            },
            updateLife(lifeExpectancy) {
                this.updateLifeExpectancy({lifeExpectancy})
            },
        }
    }
</script>


// WEBPACK FOOTER //
// Form.vue?5c906228